import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { db2 } from "../firebaseConfig";

import {
  collection,
  getDocs,
  query,
  where,
  onSnapshot,
} from "firebase/firestore";

import AllergensTranslations from "./AllergensTraductions";
import BeverageProducts from "./BeverageProductsEmp";

import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Modal,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
// ============================================================
// CONSTANTES
// ============================================================

const CATEGORY_ORDER = [
  "Sugerencias",
  "Para Picar",
  "Ensaladas",
  "Carnes",
  "Pescados",
  "Arroces",
  "Menú Infantil",
  "Postres",
];

const ALLERGENS = [
  "gluten",
  "lacteos",
  "huevo",
  "pescado",
  "crustaceos",
  "frutosSecos",
  "altramuces",
  "cacahuete",
  "apio",
  "mostaza",
  "sesamo",
  "soja",
  "moluscos",
  "dioxidoAzufre",
];

const FILTER_ALLERGENS_LABEL = {
  es: "Filtrar alérgenos",
  en: "Filter allergens",
  cat: "Filtrar al·lèrgens",
  fr: "Filtrer les allergènes",
};

const ALLERGENS_LABEL = {
  es: "Alérgenos:",
  en: "Allergens:",
  cat: "Al·lèrgens:",
  fr: "Allergènes:",
};

const TRACES_LABEL = {
  es: "Trazas:",
  en: "Traces:",
  cat: "Traces:",
  fr: "Traces:",
};

const CATEGORY_TRANSLATIONS = {
  es: {
    Sugerencias: "Sugerencias",
    "Para Picar": "Para Picar",
    Ensaladas: "Ensaladas",
    Carnes: "Carnes",
    Pescados: "Pescados",
    Arroces: "Arroces",
    "Menú Infantil": "Menú Infantil",
    Postres: "Postres",
    Bebidas: "Bebidas",
  },

  cat: {
    Sugerencias: "Suggeriments",
    "Para Picar": "Per Picar",
    Ensaladas: "Amanides",
    Carnes: "Carns",
    Pescados: "Peixos",
    Arroces: "Arròs",
    "Menú Infantil": "Menú Infantil",
    Postres: "Postres",
    Bebidas: "Begudes",
  },

  en: {
    Sugerencias: "Suggestions",
    "Para Picar": "To Snack",
    Ensaladas: "Salads",
    Carnes: "Meats",
    Pescados: "Fish",
    Arroces: "Rice Dishes",
    "Menú Infantil": "Children's Menu",
    Postres: "Desserts",
    Bebidas: "Drinks",
  },

  fr: {
    Sugerencias: "Suggestions",
    "Para Picar": "À grignoter",
    Ensaladas: "Salades",
    Carnes: "Viandes",
    Pescados: "Poissons",
    Arroces: "Plats de riz",
    "Menú Infantil": "Menu enfant",
    Postres: "Desserts",
    Bebidas: "Boissons",
  },
};

const ARROZ_TEXT = {
  es: "** Los precios de los arroces son por persona. Los arroces en paella son mínimo para 2 personas. **",
  en: "** Prices of rice dishes are per person. Paella-style rices require a minimum of 2 people. **",
  cat: "** Els preus dels arrossos són per persona. Els arrossos en paella són per a un mínim de 2 persones. **",
  fr: "** Les prix des plats de riz sont par personne. Les riz en paella sont pour un minimum de 2 personnes. **",
};

// ============================================================
// IDIOMA DEL NAVEGADOR
// ============================================================

const getBrowserLanguage = () => {
  const browserLanguage = navigator.language || navigator.userLanguage || "es";

  const languageMap = {
    ca: "cat",
  };

  const normalizedLanguage = languageMap[browserLanguage] || browserLanguage;

  const supportedLanguages = ["es", "en", "cat", "fr"];

  return (
    supportedLanguages.find((lang) => normalizedLanguage.startsWith(lang)) ||
    "es"
  );
};

// ============================================================
// SELECTOR DE IDIOMAS
// ============================================================

const LanguageSelector = ({ currentLanguage, onLanguageChange }) => {
  const languages = [
    {
      code: "es",
      label: "Español",
      flag: "/images/es.png",
    },
    {
      code: "cat",
      label: "Català",
      flag: "/images/cat.png",
    },
    {
      code: "en",
      label: "English",
      flag: "/images/en.png",
    },
    {
      code: "fr",
      label: "Français",
      flag: "/images/fr.png",
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: { xs: 0, sm: 0.5 },
      }}
    >
      {languages.map((language) => {
        const active = currentLanguage === language.code;

        return (
          <Tooltip key={language.code} title={language.label} arrow>
            <IconButton
              onClick={() => onLanguageChange(language.code)}
              sx={{
                width: {
                  xs: 31,
                  sm: 34,
                },

                height: {
                  xs: 31,
                  sm: 34,
                },

                padding: "5px",

                borderRadius: "50%",

                opacity: active ? 1 : 0.45,

                backgroundColor: active
                  ? "rgba(141,18,39,0.07)"
                  : "transparent",

                transition: "all 180ms ease",

                "&:hover": {
                  backgroundColor: "rgba(141,18,39,0.08)",

                  opacity: 1,

                  transform: "translateY(-1px)",
                },
              }}
            >
              <img
                src={language.flag}
                alt={language.label}
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  objectFit: "cover",
                  display: "block",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                }}
              />
            </IconButton>
          </Tooltip>
        );
      })}
    </Box>
  );
};

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================

const EmporiumMenu = () => {
  const { category } = useParams();

  const isMobile = useMediaQuery("(max-width:600px)");

  const [loading, setLoading] = useState(true);

  const [language, setLanguage] = useState(() => {
    return localStorage.getItem("language") || getBrowserLanguage();
  });

  const [products, setProducts] = useState([]);

  const [categories, setCategories] = useState([]);

  const [translations, setTranslations] = useState({});

  const [search, setSearch] = useState("");

  const [selectedCategory, setSelectedCategory] = useState(0);

  const [selectedProduct, setSelectedProduct] = useState(null);

  const [openModal, setOpenModal] = useState(false);

  const [selectedAllergens, setSelectedAllergens] = useState([]);

  // ==========================================================
  // ESTILOS BASE
  // ==========================================================

  const COLORS = {
    red: "#8F1025",
    redDark: "#70101F",

    background: "#F5F1EC",

    paper: "#FFFDF9",

    text: "#211E1B",

    muted: "#756D66",

    line: "rgba(33,30,27,0.10)",

    green: "#4E7657",

    gold: "#A77D3D",
  };

  // ==========================================================
  // CATEGORÍA DESDE URL
  // ==========================================================

  useEffect(() => {
    if (!category) return;

    if (category === "Bebidas") {
      setSelectedCategory("Bebidas");
      return;
    }

    const numericCategory = Number(category);

    if (Number.isInteger(numericCategory) && numericCategory >= 0) {
      setSelectedCategory(numericCategory);
      return;
    }

    const categoryIndex = categories.indexOf(category);

    if (categoryIndex >= 0) {
      setSelectedCategory(categoryIndex);
    }
  }, [category, categories]);

  // ==========================================================
  // ASEGURAR SCROLL DE TODA LA PÁGINA
  // ==========================================================

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    const previousHtmlOverflow = html.style.overflowY;

    const previousBodyOverflow = body.style.overflowY;

    const previousBodyHeight = body.style.height;

    html.style.overflowY = "auto";
    body.style.overflowY = "auto";
    body.style.height = "auto";

    return () => {
      html.style.overflowY = previousHtmlOverflow;

      body.style.overflowY = previousBodyOverflow;

      body.style.height = previousBodyHeight;
    };
  }, []);

  // ==========================================================
  // MODAL
  // ==========================================================

  const handleOpenModal = (product) => {
    setSelectedProduct(product);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedProduct(null);
  };

  // ==========================================================
  // TRADUCCIONES
  // ==========================================================

  const translateAllergen = (allergen) => {
    return AllergensTranslations?.[language]?.[allergen] || allergen;
  };

  const translateCategoryName = (categoryName) => {
    return CATEGORY_TRANSLATIONS?.[language]?.[categoryName] || categoryName;
  };

  // ==========================================================
  // FIRESTORE - TRADUCCIÓN
  // ==========================================================

  const fetchTranslation = async (codigo) => {
    try {
      const translationsRef = collection(db2, "traducciones");

      const q = query(translationsRef, where("codigo", "==", codigo));

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const data = querySnapshot.docs[0].data();

        return data.traducciones || {};
      }
    } catch (error) {
      console.error("Error obteniendo traducción:", error);
    }

    return {};
  };

  // ==========================================================
  // FIRESTORE - PRODUCTOS
  // ==========================================================

  useEffect(() => {
    if (!language) return;

    let unsubscribe = null;

    const fetchProducts = async () => {
      setLoading(true);

      try {
        const productsRef = collection(db2, "escandallos-emp");

        const q = query(
          productsRef,

          where("subfam", "in", CATEGORY_ORDER),

          where("visible", "==", true),
        );

        unsubscribe = onSnapshot(
          q,
          async (querySnapshot) => {
            try {
              const productsData = [];

              querySnapshot.forEach((doc) => {
                productsData.push(doc.data());
              });

              const translationsMap = await Promise.all(
                productsData.map(async (product) => {
                  const codigo = product.codigo;

                  const translationData = await fetchTranslation(codigo);

                  return {
                    codigo,
                    translationData,
                  };
                }),
              );

              const translationsObj = translationsMap.reduce(
                (accumulator, item) => {
                  accumulator[item.codigo] = item.translationData;

                  return accumulator;
                },
                {},
              );

              setTranslations(translationsObj);

              setProducts(productsData);

              const uniqueCategories = [
                ...new Set(productsData.map((product) => product.subfam)),
              ].sort(
                (a, b) => CATEGORY_ORDER.indexOf(a) - CATEGORY_ORDER.indexOf(b),
              );

              setCategories(uniqueCategories);

              setLoading(false);
            } catch (error) {
              console.error("Error procesando productos:", error);

              setLoading(false);
            }
          },
          (error) => {
            console.error("Error escuchando productos:", error);

            setLoading(false);
          },
        );
      } catch (error) {
        console.error("Error fetching products:", error);

        setLoading(false);
      }
    };

    fetchProducts();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [language]);

  // ==========================================================
  // PRODUCTOS FILTRADOS
  // ==========================================================

  const filteredProducts = useMemo(() => {
    const currentCategory = categories[selectedCategory];

    if (selectedCategory === "Bebidas" || !currentCategory) {
      return [];
    }

    return products
      .filter((product) =>
        (product.ingrediente || "")
          .toLowerCase()
          .includes(search.toLowerCase()),
      )
      .filter((product) => product.subfam === currentCategory)
      .filter((product) =>
        selectedAllergens.every(
          (allergen) => !(product.alergenos || []).includes(allergen),
        ),
      );
  }, [products, categories, selectedCategory, selectedAllergens, search]);

  // ==========================================================
  // EVENTOS
  // ==========================================================

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
  };

  const handleAllergenChange = (event) => {
    setSelectedAllergens(event.target.value);
  };

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);

    localStorage.setItem("language", newLanguage);
  };

  // ==========================================================
  // SPLASH
  // ==========================================================

  if (!language) {
    return (
      <Box
        sx={{
          minHeight: "100dvh",

          display: "flex",

          flexDirection: "column",

          alignItems: "center",

          justifyContent: "center",

          padding: 3,

          background: "linear-gradient(180deg,#FBF8F4,#EEE7DF)",
        }}
      >
        <img
          src="/images/logo-emporium.png"
          alt="Emporium"
          style={{
            width: "min(280px,75vw)",
            marginBottom: "28px",
          }}
        />

        <Typography
          sx={{
            fontFamily: 'Georgia, "Times New Roman", serif',

            fontSize: 26,

            marginBottom: 3,
          }}
        >
          ¡Bienvenido!
        </Typography>

        <Box
          sx={{
            display: "flex",

            flexWrap: "wrap",

            justifyContent: "center",

            gap: 1,
          }}
        >
          {[
            ["es", "Español"],
            ["cat", "Català"],
            ["en", "English"],
            ["fr", "Français"],
          ].map(([code, label]) => (
            <Button
              key={code}
              onClick={() => handleLanguageChange(code)}
              variant="contained"
              sx={{
                backgroundColor: COLORS.red,

                borderRadius: "100px",

                textTransform: "none",

                fontWeight: 700,

                px: 2.5,

                "&:hover": {
                  backgroundColor: COLORS.redDark,
                },
              }}
            >
              {label}
            </Button>
          ))}
        </Box>
      </Box>
    );
  }

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <Box
      sx={{
        width: "100%",

        minHeight: "100dvh",

        height: "auto",

        overflowX: "hidden",

        overflowY: "visible",

        background: `
          radial-gradient(
            circle at 15% 0%,
            rgba(143,16,37,0.035),
            transparent 30%
          ),
          ${COLORS.background}
        `,

        color: COLORS.text,

        fontFamily: '"Inter","Helvetica Neue",Arial,sans-serif',
      }}
    >
      {/* =====================================================
          HEADER
      ===================================================== */}

      {!loading && (
        <Box
          sx={{
            background: "#31688d",

            borderBottom: "1px solid rgba(33,30,27,0.07)",

            padding: isMobile ? "10px 13px 16px" : "13px 20px 18px",
          }}
        >
          <Container maxWidth="lg" disableGutters>
            {/* Idiomas */}

            <Box
              sx={{
                display: "flex-end",

                justifyContent: "space-between",

                alignItems: "center",

                minHeight: 32,
              }}
            >
              <LanguageSelector
                currentLanguage={language}
                onLanguageChange={handleLanguageChange}
              />
            </Box>

            {/* Logo */}

            <Box
              sx={{
                display: "flex",

                flexDirection: "column",

                alignItems: "center",

                justifyContent: "center",

                marginTop: 0.5,
                marginBottom: 3,
              }}
            >
              <img
                src="/images/logo-emporium.png"
                alt="Emporium Restaurante"
                style={{
                  display: "block",

                  width: "min(270px,68vw)",

                  maxHeight: 90,

                  objectFit: "contain",
                }}
              />
            </Box>
          </Container>
        </Box>
      )}

      {/* =====================================================
          CATEGORÍAS
      ===================================================== */}

      {!loading && (
        <Box
          sx={{
            position: "sticky",

            top: 0,

            zIndex: 50,

            background: "rgba(255,253,250,0.94)",

            backdropFilter: "blur(14px)",

            WebkitBackdropFilter: "blur(14px)",

            borderBottom: "1px solid rgba(33,30,27,0.09)",
          }}
        >
          <Container maxWidth="lg" disableGutters>
            <Tabs
              value={selectedCategory}
              onChange={handleCategoryChange}
              variant="scrollable"
              scrollButtons={false}
              allowScrollButtonsMobile
              sx={{
                minHeight: {
                  xs: 47,
                  sm: 51,
                },

                "& .MuiTabs-flexContainer": {
                  gap: "6px",

                  padding: "7px 10px",
                },

                "& .MuiTabs-indicator": {
                  display: "none",
                },

                "& .MuiTab-root": {
                  minWidth: "auto",

                  minHeight: "33px",

                  padding: "7px 14px",

                  border: "1px solid rgba(33,30,27,0.12)",

                  borderRadius: "100px",

                  color: "#5D5751",

                  background: "rgba(255,255,255,0.50)",

                  fontSize: {
                    xs: "8px",
                    sm: "9px",
                  },

                  fontWeight: 800,

                  letterSpacing: "0.04em",

                  textTransform: "uppercase",

                  transition: "all 180ms ease",
                },

                "& .MuiTab-root.Mui-selected": {
                  color: "#fff",

                  background: "#284B63",

                  borderColor: "#284B63",

                  boxShadow: "0 4px 13px rgba(143,16,37,0.18)",
                },

                "& .MuiTab-root:hover": {
                  borderColor: "#102330",

                  color: "#102330",
                },

                "& .MuiTab-root.Mui-selected:hover": {
                  color: "#fff",
                },
              }}
            >
              {CATEGORY_ORDER.map(
                (categoryName) =>
                  categories.includes(categoryName) && (
                    <Tab
                      key={categoryName}
                      value={categories.indexOf(categoryName)}
                      label={translateCategoryName(categoryName)}
                    />
                  ),
              )}

              <Tab value="Bebidas" label={translateCategoryName("Bebidas")} />
            </Tabs>
          </Container>
        </Box>
      )}

      {/* =====================================================
          LOADING
      ===================================================== */}

      {loading ? (
        <Box
          sx={{
            minHeight: "70dvh",

            display: "flex",

            flexDirection: "column",

            alignItems: "center",

            justifyContent: "center",

            gap: 1.5,

            color: COLORS.muted,
          }}
        >
          <CircularProgress
            size={28}
            thickness={3}
            sx={{
              color: COLORS.red,
            }}
          />

          <Typography
            sx={{
              fontSize: 11,

              fontWeight: 600,
            }}
          >
            {language === "es" ? "Cargando menú..." : "Loading menu..."}
          </Typography>
        </Box>
      ) : (
        <>
          {/* =================================================
              BEBIDAS
          ================================================= */}

          {selectedCategory === "Bebidas" && (
            <Box
              sx={{
                width: "100%",

                overflow: "visible",

                paddingBottom: 8,
              }}
            >
              <BeverageProducts language={language} />
            </Box>
          )}

          {/* =================================================
              RESTO DEL MENÚ
          ================================================= */}

          {selectedCategory !== "Bebidas" && (
            <Container
              maxWidth="lg"
              sx={{
                paddingTop: {
                  xs: 2.5,
                  sm: 4,
                },

                paddingBottom: 8,

                paddingLeft: {
                  xs: 1,
                  sm: 2,
                  md: 3,
                },

                paddingRight: {
                  xs: 1,
                  sm: 2,
                  md: 3,
                },
              }}
            >
              {/* =============================================
                  TÍTULO DE CATEGORÍA
              ============================================= */}

              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns: "minmax(25px,1fr) auto minmax(25px,1fr)",

                  alignItems: "center",

                  gap: {
                    xs: 1,
                    sm: 1.5,
                  },

                  maxWidth: 800,

                  margin: "0 auto 20px",
                }}
              >
                <Box
                  sx={{
                    height: "1px",

                    background:
                      "linear-gradient(90deg,transparent,rgba(33,30,27,0.18))",
                  }}
                />

                <Box
                  sx={{
                    textAlign: "center",
                  }}
                >
                  <Typography
                    component="h1"
                    sx={{
                      margin: 0,

                      fontFamily: 'Georgia,"Times New Roman",serif',

                      fontWeight: 400,

                      fontSize: {
                        xs: 27,
                        sm: 34,
                        md: 39,
                      },

                      lineHeight: 1.05,

                      whiteSpace: "nowrap",
                    }}
                  >
                    {translateCategoryName(categories[selectedCategory] || "")}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    height: "1px",

                    background:
                      "linear-gradient(90deg,rgba(33,30,27,0.18),transparent)",
                  }}
                />
              </Box>
              {/* =============================================
    FILTRO ALÉRGENOS · EMPORIUM
============================================= */}

              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: { xs: 2.5, sm: 3 },
                  px: { xs: 1.5, sm: 0 },
                }}
              >
                <FormControl
                  sx={{
                    width: {
                      xs: "100%",
                      sm: 380,
                      md: 430,
                    },
                    maxWidth: "100%",
                  }}
                >
                  <Select
                    labelId="select-allergens-label"
                    id="select-allergens"
                    multiple
                    value={selectedAllergens}
                    onChange={handleAllergenChange}
                    displayEmpty
                    /* =============================================
         CONTENIDO VISIBLE DEL SELECT
      ============================================= */

                    renderValue={(selected) => {
                      /* =============================================
           SIN FILTROS
        ============================================= */

                      if (!selected || selected.length === 0) {
                        return (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.2,
                              width: "100%",
                              minWidth: 0,
                            }}
                          >
                            <Box
                              sx={{
                                width: 34,
                                height: 34,
                                borderRadius: "50%",
                                backgroundColor: "#F5F1EC",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#284B63",
                                flexShrink: 0,
                              }}
                            >
                              <FilterAltOutlinedIcon
                                sx={{
                                  fontSize: 18,
                                }}
                              />
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                minWidth: 0,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 10,
                                  lineHeight: 1.1,
                                  fontWeight: 800,
                                  letterSpacing: "0.07em",
                                  textTransform: "uppercase",
                                  color: "#284B63",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {FILTER_ALLERGENS_LABEL[language] ||
                                  FILTER_ALLERGENS_LABEL.es}
                              </Typography>

                              <Typography
                                sx={{
                                  fontSize: 9,
                                  lineHeight: 1.2,
                                  marginTop: "3px",
                                  color: "#8A817A",
                                  fontWeight: 500,
                                }}
                              >
                                Selecciona los alérgenos
                              </Typography>
                            </Box>
                          </Box>
                        );
                      }

                      /* =============================================
           CALCULAMOS TAMAÑO SEGÚN CANTIDAD
        ============================================= */

                      const count = selected.length;

                      let iconSize = 36;
                      let imageSize = 25;
                      let radius = 11;
                      let overlap = -6;

                      if (count >= 4) {
                        iconSize = 34;
                        imageSize = 23;
                        radius = 10;
                        overlap = -7;
                      }

                      if (count >= 6) {
                        iconSize = 31;
                        imageSize = 21;
                        radius = 9;
                        overlap = -8;
                      }

                      if (count >= 8) {
                        iconSize = 28;
                        imageSize = 19;
                        radius = 8;
                        overlap = -8;
                      }

                      if (count >= 10) {
                        iconSize = 25;
                        imageSize = 17;
                        radius = 7;
                        overlap = -7;
                      }

                      /* =============================================
           TODOS LOS ALÉRGENOS SELECCIONADOS
        ============================================= */

                      return (
                        <Box
                          sx={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            overflow: "hidden",
                            minWidth: 0,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              minWidth: 0,
                              flexShrink: 1,
                            }}
                          >
                            {selected.map((allergen, index) => (
                              <Box
                                key={allergen}
                                sx={{
                                  width: {
                                    xs: iconSize - 2,
                                    sm: iconSize,
                                  },

                                  height: {
                                    xs: iconSize - 2,
                                    sm: iconSize,
                                  },

                                  minWidth: {
                                    xs: iconSize - 2,
                                    sm: iconSize,
                                  },

                                  borderRadius: `${radius}px`,

                                  backgroundColor: "#F5F1EC",

                                  border: {
                                    xs: "1.5px solid #FFFFFF",
                                    sm: "2px solid #FFFFFF",
                                  },

                                  display: "flex",

                                  alignItems: "center",

                                  justifyContent: "center",

                                  marginLeft: index === 0 ? 0 : `${overlap}px`,

                                  zIndex: selected.length - index,

                                  boxShadow: "0 2px 7px rgba(40,75,99,0.13)",

                                  flexShrink: 0,

                                  transition: "all 0.18s ease",
                                }}
                              >
                                <Box
                                  component="img"
                                  src={`/images/${allergen}.png`}
                                  alt={translateAllergen(allergen)}
                                  sx={{
                                    width: {
                                      xs: imageSize - 1,
                                      sm: imageSize,
                                    },

                                    height: {
                                      xs: imageSize - 1,
                                      sm: imageSize,
                                    },

                                    objectFit: "contain",
                                  }}
                                />
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      );
                    }}
                    /* =============================================
         ESTILO PRINCIPAL DEL SELECT
      ============================================= */

                    sx={{
                      minHeight: 58,

                      borderRadius: "17px",

                      background:
                        "linear-gradient(135deg, #FFFFFF 0%, #F5F1EC 100%)",

                      boxShadow: "0 5px 18px rgba(40,75,99,0.08)",

                      transition: "all 0.2s ease",

                      /* Interior */
                      "& .MuiSelect-select": {
                        padding: "10px 48px 10px 12px !important",

                        display: "flex",

                        alignItems: "center",

                        minHeight: "40px !important",

                        overflow: "hidden",
                      },

                      /* Borde normal */
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(40,75,99,0.13)",

                        borderWidth: 1,
                      },

                      /* Hover */
                      "&:hover": {
                        boxShadow: "0 7px 22px rgba(40,75,99,0.11)",
                      },

                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(40,75,99,0.28)",
                      },

                      /* Focus */
                      "&.Mui-focused": {
                        boxShadow: "0 7px 24px rgba(40,75,99,0.13)",
                      },

                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#284B63",
                        borderWidth: 1.5,
                      },

                      /* Flecha */
                      "& .MuiSelect-icon": {
                        color: "#284B63",
                        right: 14,
                        fontSize: 22,
                      },
                    }}
                    /* =============================================
         MENU DESPLEGABLE
      ============================================= */

                    MenuProps={{
                      PaperProps: {
                        sx: {
                          marginTop: 1,

                          padding: "6px",

                          borderRadius: "18px",

                          backgroundColor: "#FFFDF9",

                          border: "1px solid rgba(40,75,99,0.08)",

                          boxShadow: "0 15px 45px rgba(40,75,99,0.16)",

                          maxHeight: {
                            xs: 390,
                            sm: 440,
                          },

                          "& .MuiList-root": {
                            padding: 0,
                          },
                        },
                      },
                    }}
                  >
                    {/* =============================================
          LISTA DE ALÉRGENOS
      ============================================= */}

                    {ALLERGENS.map((allergen) => {
                      const isSelected = selectedAllergens.includes(allergen);

                      return (
                        <MenuItem
                          key={allergen}
                          value={allergen}
                          sx={{
                            minHeight: 58,

                            borderRadius: "14px",

                            margin: "3px 2px",

                            padding: "7px 10px",

                            transition:
                              "background-color 0.18s ease, transform 0.18s ease",

                            backgroundColor: isSelected
                              ? "rgba(40,75,99,0.07)"
                              : "transparent",

                            "&:hover": {
                              backgroundColor: "rgba(40,75,99,0.055)",
                            },

                            "&.Mui-selected": {
                              backgroundColor: "rgba(40,75,99,0.07)",
                            },

                            "&.Mui-selected:hover": {
                              backgroundColor: "rgba(40,75,99,0.10)",
                            },

                            "&:active": {
                              transform: "scale(0.985)",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: "100%",

                              display: "flex",

                              alignItems: "center",

                              justifyContent: "space-between",
                            }}
                          >
                            {/* =============================================
                  INFORMACIÓN DEL ALÉRGENO
              ============================================= */}

                            <Box
                              sx={{
                                display: "flex",

                                alignItems: "center",

                                gap: 1.3,

                                minWidth: 0,
                              }}
                            >
                              {/* Icono */}
                              <Box
                                sx={{
                                  width: 38,

                                  height: 38,

                                  borderRadius: "12px",

                                  display: "flex",

                                  alignItems: "center",

                                  justifyContent: "center",

                                  backgroundColor: isSelected
                                    ? "#FFFFFF"
                                    : "#F5F1EC",

                                  boxShadow: isSelected
                                    ? "0 2px 8px rgba(40,75,99,0.08)"
                                    : "none",

                                  transition: "all 0.18s ease",

                                  flexShrink: 0,
                                }}
                              >
                                <Box
                                  component="img"
                                  src={`/images/${allergen}.png`}
                                  alt={allergen}
                                  sx={{
                                    width: 25,

                                    height: 25,

                                    objectFit: "contain",
                                  }}
                                />
                              </Box>

                              {/* Nombre */}
                              <Typography
                                sx={{
                                  fontSize: 10.5,

                                  fontWeight: isSelected ? 800 : 650,

                                  color: "#393532",

                                  lineHeight: 1.2,
                                }}
                              >
                                {translateAllergen(allergen)}
                              </Typography>
                            </Box>

                            {/* =============================================
                  CHECK CIRCULAR
              ============================================= */}

                            <Box
                              sx={{
                                width: 25,

                                height: 25,

                                borderRadius: "50%",

                                display: "flex",

                                alignItems: "center",

                                justifyContent: "center",

                                border: isSelected
                                  ? "2px solid #284B63"
                                  : "1.5px solid #C9C2BC",

                                backgroundColor: isSelected
                                  ? "#284B63"
                                  : "transparent",

                                transition: "all 0.18s ease",

                                flexShrink: 0,

                                marginLeft: 1,
                              }}
                            >
                              {isSelected && (
                                <Typography
                                  sx={{
                                    color: "#FFFFFF",

                                    fontSize: 14,

                                    fontWeight: 900,

                                    lineHeight: 1,

                                    transform: "translateY(-0.5px)",
                                  }}
                                >
                                  ✓
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Box>
              {/* =============================================
                  PRODUCTOS
              ============================================= */}

              <Box
                sx={{
                  width: "100%",

                  display: "flex",

                  flexDirection: "column",

                  border: "1px solid rgba(33,30,27,0.07)",

                  borderRadius: {
                    xs: 1.8,
                    sm: 2,
                  },

                  overflow: "hidden",

                  background: "rgba(255,253,249,0.72)",

                  boxShadow: "0 4px 20px rgba(40,31,25,0.025)",
                }}
              >
                {filteredProducts.length === 0 ? (
                  <Box
                    sx={{
                      padding: "60px 20px",

                      textAlign: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Georgia,"Times New Roman",serif',

                        fontSize: 23,

                        marginBottom: 1,

                        fontWeight: 400,
                      }}
                    >
                      {language === "es" ? "No hay productos" : "No products"}
                    </Typography>

                    <Typography
                      sx={{
                        color: COLORS.muted,

                        fontSize: 11,
                      }}
                    >
                      {language === "es"
                        ? "No encontramos productos con estos filtros."
                        : "No products match these filters."}
                    </Typography>
                  </Box>
                ) : (
                  filteredProducts.map((product, index) => {
                    const translatedName =
                      translations[product.codigo]?.[language]?.name ||
                      product.ingrediente;

                    const translatedDescription =
                      translations[product.codigo]?.[language]?.description ||
                      product.comentarios;

                    const traces = (product.trazas || []).filter(
                      (trace) => !(product.alergenos || []).includes(trace),
                    );

                    const price = product.precioVentaSugerido
                      ? Number(product.precioVentaSugerido).toFixed(2)
                      : "0.00";

                    return (
                      <Box
                        key={product.codigo || index}
                        sx={{
                          display: "grid",

                          gridTemplateColumns: {
                            xs: "94px minmax(0,1fr)",
                            sm: "145px minmax(0,1fr)",
                          },

                          gap: {
                            xs: 1.5,
                            sm: 2.2,
                          },

                          padding: {
                            xs: 1.5,
                            sm: 2,
                          },

                          borderBottom:
                            index < filteredProducts.length - 1
                              ? `1px solid ${COLORS.line}`
                              : "none",

                          background: "rgba(241, 241, 243, 0.72)",

                          transition: "background 180ms ease",

                          "&:hover": {
                            background: "rgba(255,255,255,0.96)",
                          },
                        }}
                      >
                        {/* FOTO */}

                        <Box
                          onClick={() => handleOpenModal(product)}
                          sx={{
                            position: "relative",

                            width: "100%",

                            height: {
                              xs: 101,
                              sm: 125,
                            },

                            borderRadius: {
                              xs: 1.2,
                              sm: 1.5,
                            },

                            overflow: "hidden",

                            background: "#EDE7E0",

                            cursor: "pointer",

                            flexShrink: 0,

                            "&:hover img": {
                              transform: "scale(1.04)",
                            },
                          }}
                        >
                          <img
                            src={
                              product.imagen || "/images/default-escandallo.jpg"
                            }
                            alt={translatedName}
                            style={{
                              width: "100%",

                              height: "100%",

                              objectFit: "cover",

                              display: "block",

                              transition: "transform 400ms ease",
                            }}
                          />

                          {/* Degradado */}

                          <Box
                            sx={{
                              position: "absolute",

                              inset: 0,

                              pointerEvents: "none",

                              background:
                                "linear-gradient(180deg,transparent 55%,rgba(0,0,0,0.20))",
                            }}
                          />

                          {/* PRECIO */}
                        </Box>

                        {/* INFORMACIÓN */}

                        <Box
                          sx={{
                            minWidth: 0,

                            display: "flex",

                            flexDirection: "column",

                            justifyContent: "center",
                          }}
                        >
                          {/* Nombre + precio desktop */}

                          <Box
                            sx={{
                              display: "flex",

                              alignItems: "flex-start",

                              justifyContent: "space-between",

                              gap: 1,
                            }}
                          >
                            <Typography
                              component="h2"
                              sx={{
                                margin: 0,

                                fontFamily: 'Georgia,"Times New Roman",serif',

                                fontWeight: 400,

                                fontSize: {
                                  xs: 17,
                                  sm: 21,
                                  md: 23,
                                },

                                lineHeight: 1.12,

                                color: COLORS.text,
                              }}
                            >
                              {translatedName}
                            </Typography>

                            <Typography
                              sx={{
                                display: {
                                  xs: "none",
                                  sm: "block",
                                },

                                color: COLORS.red,

                                fontSize: 15,

                                fontWeight: 900,

                                whiteSpace: "nowrap",

                                paddingTop: "2px",
                              }}
                            >
                              €{price}
                            </Typography>
                          </Box>

                          {/* Descripción */}

                          {translatedDescription && (
                            <Typography
                              sx={{
                                marginTop: 0.6,

                                marginBottom: 0.8,

                                color: COLORS.muted,

                                fontSize: {
                                  xs: 10,
                                  sm: 11,
                                },

                                lineHeight: 1.45,

                                display: "-webkit-box",

                                overflow: "hidden",

                                WebkitLineClamp: {
                                  xs: 3,
                                  sm: 4,
                                },

                                WebkitBoxOrient: "vertical",
                              }}
                            >
                              {translatedDescription}
                            </Typography>
                          )}

                          {/* VEGAN / VEGETARIANO */}

                          {(product.isVegan || product.isVegetarian) && (
                            <Box
                              sx={{
                                display: "flex",

                                flexWrap: "wrap",

                                gap: 0.6,

                                marginBottom: 0.6,
                              }}
                            >
                              {product.isVegan && (
                                <Box
                                  sx={{
                                    display: "inline-flex",

                                    alignItems: "center",

                                    gap: 0.5,

                                    padding: "3px 7px",

                                    borderRadius: "100px",

                                    background: "rgba(78,118,87,0.08)",

                                    color: COLORS.green,

                                    fontSize: 8,

                                    fontWeight: 800,
                                  }}
                                >
                                  <img
                                    src="/images/vegan.png"
                                    alt="Vegan"
                                    style={{
                                      width: 14,
                                      height: 14,
                                      objectFit: "contain",
                                    }}
                                  />
                                  Vegan
                                </Box>
                              )}

                              {product.isVegetarian && (
                                <Box
                                  sx={{
                                    display: "inline-flex",

                                    alignItems: "center",

                                    gap: 0.5,

                                    padding: "3px 7px",

                                    borderRadius: "100px",

                                    background: "rgba(167,125,61,0.09)",

                                    color: COLORS.gold,

                                    fontSize: 8,

                                    fontWeight: 800,
                                  }}
                                >
                                  <img
                                    src="/images/vegetarian.png"
                                    alt="Vegetarian"
                                    style={{
                                      width: 14,
                                      height: 14,
                                      objectFit: "contain",
                                    }}
                                  />
                                  Vegetarian
                                </Box>
                              )}
                            </Box>
                          )}

                          {/* ALÉRGENOS */}

                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              alignItems: "center",
                              gap: 0.55,
                              marginTop: 0.35,
                            }}
                          >
                            <Typography
                              sx={{
                                color: "#284B63",
                                fontWeight: 800,
                                fontSize: {
                                  xs: 7,
                                  sm: 8,
                                },
                                letterSpacing: "0.04em",
                                marginRight: 0.25,
                                lineHeight: 1,
                              }}
                            >
                              {ALLERGENS_LABEL[language] || ALLERGENS_LABEL.es}
                            </Typography>

                            {(product.alergenos || []).map((allergen) => (
                              <Box
                                key={allergen}
                                title={translateAllergen(allergen)}
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.4,

                                  padding: "2px 6px 2px 3px",

                                  border: "1px solid rgba(33,30,27,0.07)",
                                  borderRadius: "999px",

                                  background:
                                    "linear-gradient(135deg, #FFFFFF 0%, #F8F4EF 100%)",

                                  boxShadow: "0 1px 3px rgba(33,30,27,0.04)",

                                  minHeight: {
                                    xs: 20,
                                    sm: 22,
                                  },

                                  transition: "all 0.18s ease",

                                  "&:hover": {
                                    borderColor: "rgba(147,57,28,0.18)",
                                    boxShadow: "0 2px 6px rgba(33,30,27,0.08)",
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    width: {
                                      xs: 16,
                                      sm: 18,
                                    },

                                    height: {
                                      xs: 16,
                                      sm: 18,
                                    },

                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",

                                    borderRadius: "50%",

                                    backgroundColor: "#FFFFFF",

                                    flexShrink: 0,
                                  }}
                                >
                                  <Box
                                    component="img"
                                    src={`/images/${allergen}.png`}
                                    alt={translateAllergen(allergen)}
                                    sx={{
                                      width: {
                                        xs: 12,
                                        sm: 13,
                                      },

                                      height: {
                                        xs: 12,
                                        sm: 13,
                                      },

                                      objectFit: "contain",
                                    }}
                                  />
                                </Box>

                                <Typography
                                  sx={{
                                    color: "#393532",

                                    fontSize: {
                                      xs: 7,
                                      sm: 8,
                                    },

                                    fontWeight: 700,

                                    lineHeight: 1,

                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {translateAllergen(allergen)}
                                </Typography>
                              </Box>
                            ))}
                          </Box>

                          {/* TRAZAS */}

                          {traces.length > 0 && (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                alignItems: "center",
                                gap: 0.55,
                                marginTop: 0.5,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: "#284B63",
                                  fontWeight: 800,
                                  fontSize: {
                                    xs: 7,
                                    sm: 8,
                                  },
                                  letterSpacing: "0.04em",
                                  marginRight: 0.25,
                                  lineHeight: 1,
                                }}
                              >
                                {TRACES_LABEL[language] || TRACES_LABEL.es}
                              </Typography>

                              {traces.map((allergen) => (
                                <Box
                                  key={allergen}
                                  title={translateAllergen(allergen)}
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.4,

                                    padding: "2px 6px 2px 3px",

                                    border: "1px solid rgba(33,30,27,0.06)",

                                    borderRadius: "999px",

                                    backgroundColor: "rgba(255,255,255,0.65)",

                                    minHeight: {
                                      xs: 20,
                                      sm: 22,
                                    },

                                    opacity: 0.62,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: {
                                        xs: 16,
                                        sm: 18,
                                      },

                                      height: {
                                        xs: 16,
                                        sm: 18,
                                      },

                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",

                                      borderRadius: "50%",

                                      backgroundColor: "#FFFFFF",

                                      flexShrink: 0,
                                    }}
                                  >
                                    <Box
                                      component="img"
                                      src={`/images/${allergen}.png`}
                                      alt={translateAllergen(allergen)}
                                      sx={{
                                        width: {
                                          xs: 12,
                                          sm: 13,
                                        },

                                        height: {
                                          xs: 12,
                                          sm: 13,
                                        },

                                        objectFit: "contain",
                                      }}
                                    />
                                  </Box>

                                  <Typography
                                    sx={{
                                      color: "#393532",

                                      fontSize: {
                                        xs: 7,
                                        sm: 8,
                                      },

                                      fontWeight: 650,

                                      lineHeight: 1,

                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {translateAllergen(allergen)}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}

                          {/* INDICACIÓN */}

                          <Typography
                            onClick={() => handleOpenModal(product)}
                            sx={{
                              display: {
                                sm: "inline-flex",
                              },

                              alignItems: "center",

                              marginTop: 0.9,

                              color: "#284B63",

                              fontSize: 7.5,

                              fontWeight: 900,

                              letterSpacing: "0.14em",

                              textTransform: "uppercase",

                              cursor: "pointer",

                              position: "relative",

                              width: "fit-content",

                              transition: "color 180ms ease",

                              "&::after": {
                                content: '""',

                                position: "absolute",

                                left: 0,

                                bottom: -3,

                                width: "100%",

                                height: "1px",

                                backgroundColor: "#284B63",

                                transformOrigin: "left",

                                transform: "scaleX(0.35)",

                                transition: "transform 180ms ease",
                              },

                              "&:hover": {
                                color: "#19354D",
                              },

                              "&:hover::after": {
                                transform: "scaleX(1)",
                                backgroundColor: "#19354D",
                              },
                            }}
                          >
                            Descubrir
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>

              {/* =============================================
                  AVISO ARROCES
              ============================================= */}

              {categories[selectedCategory] === "Arroces" && (
                <Box
                  sx={{
                    display: "flex",

                    justifyContent: "center",

                    alignItems: "center",

                    maxWidth: 720,

                    margin: "16px auto 0",

                    padding: "10px 14px",

                    border: "1px solid rgba(143,16,37,0.11)",

                    borderRadius: 1.2,

                    background: "rgba(143,16,37,0.028)",
                  }}
                >
                  <Typography
                    sx={{
                      margin: 0,

                      textAlign: "center",

                      color: COLORS.muted,

                      fontSize: {
                        xs: 8,
                        sm: 9,
                      },

                      lineHeight: 1.5,
                    }}
                  >
                    {ARROZ_TEXT[language]}
                  </Typography>
                </Box>
              )}
            </Container>
          )}
        </>
      )}

      {/* =====================================================
          MODAL PRODUCTO
      ===================================================== */}

      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="modal-title"
      >
        <Box
          onClick={handleCloseModal}
          sx={{
            position: "fixed",

            inset: 0,

            display: "flex",

            justifyContent: "center",

            alignItems: {
              xs: "flex-end",
              sm: "center",
            },

            padding: {
              xs: 0,
              sm: 2.5,
            },

            background: "rgba(22,18,16,0.72)",
          }}
        >
          {selectedProduct && (
            <Box
              onClick={(event) => event.stopPropagation()}
              sx={{
                position: "relative",

                width: "100%",

                maxWidth: 640,

                maxHeight: {
                  xs: "92dvh",
                  sm: "calc(100dvh - 40px)",
                },

                overflowY: "auto",

                background: COLORS.paper,

                borderRadius: {
                  xs: "18px 18px 0 0",
                  sm: 2.5,
                },

                boxShadow: "0 25px 80px rgba(0,0,0,0.30)",
              }}
            >
              {/* Cerrar */}

              <IconButton
                onClick={handleCloseModal}
                sx={{
                  position: "absolute",

                  top: 10,

                  right: 10,

                  zIndex: 10,

                  width: 35,

                  height: 35,

                  background: "rgba(255,255,255,0.94)",

                  color: "#302C29",

                  boxShadow: "0 3px 12px rgba(0,0,0,0.14)",

                  "&:hover": {
                    background: "#fff",
                  },
                }}
              >
                ×
              </IconButton>

              {/* Imagen */}

              <Box
                sx={{
                  width: "100%",

                  height: {
                    xs: 250,
                    sm: 330,
                  },

                  overflow: "hidden",
                }}
              >
                <img
                  src={
                    selectedProduct.imagen || "/images/default-escandallo.jpg"
                  }
                  alt={selectedProduct.ingrediente}
                  style={{
                    width: "100%",

                    height: "100%",

                    objectFit: "cover",

                    display: "block",
                  }}
                />
              </Box>

              {/* Información */}

              <Box
                sx={{
                  padding: {
                    xs: "21px 18px 28px",
                    sm: "25px 28px 30px",
                  },
                }}
              >
                <Typography
                  sx={{
                    color: COLORS.red,

                    fontSize: 8,

                    fontWeight: 900,

                    letterSpacing: "0.22em",

                    textTransform: "uppercase",

                    marginBottom: 0.5,
                  }}
                >
                  {selectedProduct.subfam}
                </Typography>

                <Box
                  sx={{
                    display: "flex",

                    justifyContent: "space-between",

                    gap: 2,

                    alignItems: "flex-start",
                  }}
                >
                  <Typography
                    id="modal-title"
                    component="h2"
                    sx={{
                      margin: 0,

                      fontFamily: 'Georgia,"Times New Roman",serif',

                      fontSize: {
                        xs: 27,
                        sm: 35,
                      },

                      lineHeight: 1.08,

                      fontWeight: 400,
                    }}
                  >
                    {translations[selectedProduct.codigo]?.[language]?.name ||
                      selectedProduct.ingrediente}
                  </Typography>

                  <Typography
                    sx={{
                      color: COLORS.red,

                      fontSize: 19,

                      fontWeight: 900,

                      whiteSpace: "nowrap",

                      paddingTop: 0.5,
                    }}
                  >
                    €
                    {selectedProduct.precioVentaSugerido
                      ? Number(selectedProduct.precioVentaSugerido).toFixed(2)
                      : "0.00"}
                  </Typography>
                </Box>

                {/* Descripción */}

                {(translations[selectedProduct.codigo]?.[language]
                  ?.description ||
                  selectedProduct.comentarios) && (
                  <Typography
                    id="modal-description"
                    sx={{
                      marginTop: 1.2,

                      color: COLORS.muted,

                      fontSize: 13,

                      lineHeight: 1.65,
                    }}
                  >
                    {translations[selectedProduct.codigo]?.[language]
                      ?.description || selectedProduct.comentarios}
                  </Typography>
                )}

                {/* Dieta */}

                {(selectedProduct.isVegan || selectedProduct.isVegetarian) && (
                  <Box
                    sx={{
                      display: "flex",

                      flexWrap: "wrap",

                      gap: 1,

                      marginTop: 1.5,
                    }}
                  >
                    {selectedProduct.isVegan && (
                      <Box
                        sx={{
                          display: "inline-flex",

                          alignItems: "center",

                          gap: 0.6,

                          padding: "5px 9px",

                          borderRadius: "100px",

                          background: "rgba(78,118,87,0.08)",

                          color: COLORS.green,

                          fontSize: 9,

                          fontWeight: 800,
                        }}
                      >
                        <img
                          src="/images/vegan.png"
                          alt="Vegan"
                          style={{
                            width: 16,
                            height: 16,
                          }}
                        />
                        Vegan
                      </Box>
                    )}

                    {selectedProduct.isVegetarian && (
                      <Box
                        sx={{
                          display: "inline-flex",

                          alignItems: "center",

                          gap: 0.6,

                          padding: "5px 9px",

                          borderRadius: "100px",

                          background: "rgba(167,125,61,0.09)",

                          color: COLORS.gold,

                          fontSize: 9,

                          fontWeight: 800,
                        }}
                      >
                        <img
                          src="/images/vegetarian.png"
                          alt="Vegetarian"
                          style={{
                            width: 16,
                            height: 16,
                          }}
                        />
                        Vegetarian
                      </Box>
                    )}
                  </Box>
                )}

                {/* Separador */}

                <Divider
                  sx={{
                    margin: "18px 0",
                  }}
                />

                {/* ALÉRGENOS */}

                <Typography
                  sx={{
                    fontSize: 9,

                    fontWeight: 900,

                    letterSpacing: "0.12em",

                    textTransform: "uppercase",

                    marginBottom: 1,
                  }}
                >
                  {ALLERGENS_LABEL[language] || ALLERGENS_LABEL.es}
                </Typography>

                <Box
                  sx={{
                    display: "flex",

                    flexWrap: "wrap",

                    gap: 0.7,
                  }}
                >
                  {(selectedProduct.alergenos || []).map((allergen) => (
                    <Box
                      key={allergen}
                      sx={{
                        display: "inline-flex",

                        alignItems: "center",

                        gap: 0.6,

                        padding: "5px 8px",

                        borderRadius: 0.8,

                        background: "#F1ECE6",

                        color: "#5F5953",

                        fontSize: 9,
                      }}
                    >
                      <img
                        src={`/images/${allergen}.png`}
                        alt={translateAllergen(allergen)}
                        style={{
                          width: 17,
                          height: 17,
                          objectFit: "contain",
                        }}
                      />

                      {translateAllergen(allergen)}
                    </Box>
                  ))}
                </Box>

                {/* TRAZAS */}

                {(selectedProduct.trazas || []).filter(
                  (trace) => !(selectedProduct.alergenos || []).includes(trace),
                ).length > 0 && (
                  <>
                    <Typography
                      sx={{
                        marginTop: 2,

                        fontSize: 9,

                        fontWeight: 900,

                        letterSpacing: "0.12em",

                        textTransform: "uppercase",

                        marginBottom: 1,
                      }}
                    >
                      {TRACES_LABEL[language] || TRACES_LABEL.es}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",

                        flexWrap: "wrap",

                        gap: 0.7,
                      }}
                    >
                      {(selectedProduct.trazas || [])
                        .filter(
                          (trace) =>
                            !(selectedProduct.alergenos || []).includes(trace),
                        )
                        .map((allergen) => (
                          <Box
                            key={allergen}
                            sx={{
                              display: "inline-flex",

                              alignItems: "center",

                              gap: 0.6,

                              padding: "5px 8px",

                              borderRadius: 0.8,

                              background: "#F1ECE6",

                              color: "#5F5953",

                              fontSize: 9,

                              opacity: 0.72,
                            }}
                          >
                            <img
                              src={`/images/${allergen}.png`}
                              alt={translateAllergen(allergen)}
                              style={{
                                width: 17,
                                height: 17,
                                objectFit: "contain",
                              }}
                            />

                            {translateAllergen(allergen)}
                          </Box>
                        ))}
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default EmporiumMenu;
